import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useRequest from "../../components/hooks/use-request";
import { formatDate } from "../../utilities/functions";

const avatarColors = ["#0979A1", "#E85D04", "#7B2D8B", "#2D6A4F", "#D62828", "#023E8A"];
const avatarColor = (name: string) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];

const UserCell = ({ name, email }: { name: string; email?: string }) => (
  <div className="flex items-center gap-2">
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-semibold shrink-0"
      style={{ backgroundColor: avatarColor(name) }}>
      {name?.charAt(0).toUpperCase()}
    </div>
    <div>
      <p className="text-[13px] font-medium">{name}</p>
      {email && <p className="text-[11px] text-gray-500">{email}</p>}
    </div>
  </div>
);

const VoucherRedemptions = () => {
  const userToken = localStorage.getItem("token");
  const [redemptions, setRedemptions] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const { makeRequest } = useRequest("/campaigns/vouchers/redemption-history", "GET", {
    Authorization: `Bearer ${userToken}`,
  });

  useEffect(() => {
    const fetch = async () => {
      const [res] = await makeRequest(undefined, { page: currentPage });
      if (res?.data) {
        setRedemptions(res.data.history || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalItems(res.data.total || 0);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return (
    <>
      <div className="w-[95%]">
        <p className="text-[13px] text-gray-500 mb-1">
          <Link to="/campaign" className="text-[#0979A1] font-medium">Campaign Overview</Link>
          {" / Voucher Redemptions History"}
        </p>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 mt-4">
          <h3 className="text-[18px] font-semibold mb-4">Voucher Redemption History</h3>

          <table className="w-full text-[13px]">
            <thead className="bg-[#CFF0FC] h-10">
              <tr className="text-left">
                <th className="px-3 font-medium">Merchant</th>
                <th className="px-3 font-medium">Buyer</th>
                <th className="px-3 font-medium">Amount (₦)</th>
                <th className="px-3 font-medium">Transaction ID</th>
                <th className="px-3 font-medium">Redeemed On</th>
              </tr>
            </thead>
            <tbody>
              {redemptions.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-10 text-center text-gray-500">
                    No redemptions yet
                  </td>
                </tr>
              )}
              {redemptions.map((r: any, i: number) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-3">
                    <UserCell name={r.merchantName} email={r.merchantEmail} />
                  </td>
                  <td className="px-3 py-3">
                    <UserCell name={r.buyerName || r.buyerEmail} email={r.buyerName ? r.buyerEmail : undefined} />
                  </td>
                  <td className="px-3 py-3">{(r.amount || 0).toLocaleString()}</td>
                  <td className="px-3 py-3">
                    <span className="text-[#0979A1] font-medium">{r.transactionId}</span>
                  </td>
                  <td className="px-3 py-3">{r.redeemedOn ? formatDate(r.redeemedOn) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 flex justify-between items-center text-[13px] text-gray-600">
            <span>1 — {Math.min(redemptions.length, 10)} of {totalItems} results</span>
            <div className="flex items-center gap-3">
              <span>{currentPage} of {totalPages} pages</span>
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 hover:bg-gray-50">Prev</button>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-200 rounded-md disabled:opacity-40 hover:bg-gray-50">Next</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VoucherRedemptions;
